import { useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, Users } from 'lucide-react';
import type {
  AppointmentTypeRecommendation,
  Prioritization,
  RankedProvider,
} from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { RankedProviderList, ProviderRankingReasons } from '@/components/custom/provider-ranking';
import { describeContribution } from '@/components/custom/appointment-type-recommendation';

interface AppointmentTypeResultsProps {
  result: AppointmentTypeRecommendation;
  /** The mix the run actually used — so the composite arithmetic shown matches it. */
  prioritization: Prioritization;
  /** Highlights the provider chosen, when a host lets one be chosen. */
  selectedProviderId?: string | undefined;
  /** The chosen row's location: a provider ranked at two locations is two rows. */
  selectedLocationId?: string | undefined;
  /** When set, provider rows can be clicked (the campaign queue's slots panel). */
  onSelectProvider?: ((provider: RankedProvider) => void) | undefined;
}

/**
 * The shared results screen for a referral's provider pool + type scoring.
 *
 * Rendered identically by the Appointment Type Simulator and the Referrals
 * worklist "Why these providers?" popover, so staff comparing the two never read
 * them as two different algorithms. The ranked pool is the headline; the two
 * collapsibles below it ("Why this order?" and "How did each type score?") keep
 * the justification out of the way until asked for.
 *
 * Ties render read-only here — the type is picked when the appointment is
 * actually booked, not from this explanation view.
 */
export function AppointmentTypeResults({
  result,
  prioritization,
  selectedProviderId,
  selectedLocationId,
  onSelectProvider,
}: AppointmentTypeResultsProps): React.ReactElement {
  const { explanation } = result;
  const [showProviderReasons, setShowProviderReasons] = useState(false);
  const [showTypeReasons, setShowTypeReasons] = useState(false);
  const consideredTypes = explanation.allTypes;
  const eliminatedCount = consideredTypes.filter((t) => t.eliminated).length;

  return (
    <div className="space-y-4">
      {/* Providers — the headline result, each WITH the type they'd be booked as
          (or a read-only tie). Everything below explains HOW this order was
          reached; this is what an operator acts on. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">Providers to offer</h4>
        </div>
        <RankedProviderList
          providers={explanation.providers}
          selectedProviderId={selectedProviderId}
          selectedLocationId={selectedLocationId}
          onSelect={onSelectProvider}
        />
        {explanation.providers.length > 0 && (
          <Collapsible open={showProviderReasons} onOpenChange={setShowProviderReasons}>
            <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform',
                  showProviderReasons && 'rotate-180'
                )}
              />
              {showProviderReasons ? 'Hide reasons' : 'Why this order?'}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <ProviderRankingReasons
                providers={explanation.providers}
                prioritization={prioritization}
              />
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>

      {/* Fallback notice */}
      {explanation.fallbackPassUsed > 1 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          No exact code match was found. Results are based on other matching criteria.
        </div>
      )}

      {/* Per-type breakdown — read-only diagnostic. The winner/tie decision lives
          on each provider row above; this only explains how each type scored and
          why any were eliminated. */}
      {consideredTypes.length > 0 && (
        <Collapsible open={showTypeReasons} onOpenChange={setShowTypeReasons}>
          <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', showTypeReasons && 'rotate-180')}
            />
            {showTypeReasons ? 'Hide type scoring' : 'How did each type score?'}
            {eliminatedCount > 0 && !showTypeReasons && (
              <span className="text-muted-foreground/70"> ({eliminatedCount} not eligible)</span>
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-2">
            {consideredTypes.map((type) => {
              const positives = type.contributions.filter((c) => c.points > 0);
              const negatives = type.contributions.filter((c) => c.points < 0);
              return (
                <div
                  key={type.appointmentTypeId}
                  className={cn(
                    'space-y-1 rounded-lg border p-2.5',
                    type.eliminated && 'opacity-70'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{type.appointmentTypeName}</span>
                    {type.eliminated && (
                      <Badge variant="outline" className="shrink-0 text-xs text-muted-foreground">
                        Not eligible{type.eliminationReason ? ` · ${type.eliminationReason}` : ''}
                      </Badge>
                    )}
                  </div>
                  {positives.map((c, i) => (
                    <p
                      key={`p${i}`}
                      className="flex items-start gap-1.5 text-xs text-muted-foreground"
                    >
                      <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-success" />
                      {describeContribution(c)}
                    </p>
                  ))}
                  {negatives.map((c, i) => (
                    <p
                      key={`n${i}`}
                      className="flex items-start gap-1.5 text-xs text-muted-foreground"
                    >
                      <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />
                      {describeContribution(c)}
                    </p>
                  ))}
                  {!type.eliminated && positives.length === 0 && negatives.length === 0 && (
                    <p className="text-xs text-muted-foreground">No specific matching signals</p>
                  )}
                </div>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
